<template>
  <div class="bp-select" :class="componentClasses">
    <label
      :for="id"
      class="bp-select__label"
      :class="labelClasses"
      v-if="label"
    >
      {{ label }}
      <span v-if="required" class="text-negative-700">*</span>
    </label>

    <bp-select-popper
      trigger="clickToToggle"
      :disabled="_disabled"
      :options="popperOptions"
    >
      <div
        slot="reference"
        ref="popperContainer"
        class="bp-select__container"
        :id="id"
      >
        <bp-icon
          v-if="_preIcon"
          :icon="_preIcon"
          :size="iconSize"
          class="text-neutral-600"
        />

        <input
          v-model="innerText"
          ref="input"
          type="text"
          class="bp-select__inner"
          :class="inputClasses"
          :readonly="!filterable"
          :placeholder="placeholder"
        />

        <bp-icon
          icon="fa-light fa-angle-down"
          class="text-neutral-600"
          :size="iconSize"
        />
      </div>

      <div class="bp-select__options" slot="default">
        <div class="text-center pt-3" v-if="loading">
          <b-spinner variant="primary" />
        </div>
        <span
          class="bp-select__option-cell disabled"
          v-else-if="!options.length"
        >
          Nenhuma opção disponível
        </span>

        <template v-else>
          <div
            v-for="option in options.filter(opt => !opt.hide)"
            :key="option.id"
          >
            <div
              class="bp-select__option-cell"
              :class="{
                disabled: option.disabled,
                selected: isSelected(option)
              }"
              @click="selectOption(option)"
            >
              <slot :name="option.id">
                <span class="bp-select__option-name">
                  {{ option.name || option.id }}
                </span>
              </slot>
            </div>
          </div>
        </template>
      </div>
    </bp-select-popper>

    <div
      v-if="description || errorMessage"
      class="bp-select__description-container"
      :class="descriptionContainerClasses"
    >
      <div class="bp-select__description">
        {{ errorMessage || description }}
      </div>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";
export default {
  components: { "bp-select-popper": Popper },
  props: {
    value: {
      type: [String, Number, Array]
    },
    id: {
      type: String,
      default: "bp-select"
    },
    size: {
      type: String,
      default: "lg",
      validation: val => ["xl", "lg", "md", "sm"].includes(val)
    },
    label: {
      type: String
    },
    preIcon: {
      type: String
    },
    placeholder: {
      type: String,
      default: "Selecionar"
    },
    description: {
      type: String
    },
    required: {
      type: Boolean
    },
    errors: {
      type: [Array, String]
    },
    filterable: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: this.value,
      innerText: "",
      accessed: false
    };
  },
  methods: {
    selectOption(option) {
      this.$emit("change", option);
      if (!option.disabled) {
        this.innerValue = option.id;
        this.setInnerText();
        this.$refs.popperContainer.click();
      }
    },
    isSelected(option) {
      return this.innerValue === option.id;
    },
    setInnerText() {
      this.innerText =
        this.options.find(opt => opt.id === this.innerValue)?.name || "";
    }
  },
  computed: {
    _disabled() {
      return this.disabled || this.loading;
    },
    _preIcon() {
      if (this.loading) return "fa-solid fa-spinner-third fa-spin";
      return this.preIcon;
    },
    computedSize() {
      return ["xl", "lg", "md", "sm"].includes(this.size) ? this.size : "lg";
    },
    errorMessage() {
      if (this.errors && this.errors.length) {
        if (_.isArray(this.errors)) return this.errors[0];
        return this.errors;
      }

      if (this.accessed && this.required && !this.innerValue)
        return "Esse campo é obrigatório.";

      return undefined;
    },

    popperOptions() {
      return {
        placement: "bottom",
        modifiers: {
          offset: {
            offset: "0,10px"
          }
        }
      };
    },

    // Computed classes
    componentClasses() {
      const classes = [];

      classes.push(this.computedSize);
      if (this.disabled) classes.push("disabled");

      if (this._disabled) classes.push("disabled");
      if (this.errorMessage) classes.push("error");

      return classes;
    },
    labelClasses() {
      const classes = [];
      classes.push(
        ["xl", "lg"].includes(this.computedSize)
          ? "functional-1"
          : "functional-2"
      );
      return classes;
    },
    inputClasses() {
      const classes = [];

      const spacing = {
        xl: "px-5",
        lg: "px-4",
        md: "px-4",
        sm: "px-3"
      };
      classes.push(spacing[this.computedSize]);

      switch (this.computedSize) {
        case "md":
          classes.push("functional-1");
          break;
        case "sm":
          classes.push("functional-2");
          break;
      }
      return classes;
    },
    descriptionContainerClasses() {
      const classes = [];

      classes.push(
        ["xl", "lg"].includes(this.computedSize)
          ? "functional-1"
          : "functional-2"
      );

      return classes;
    },
    iconSize() {
      const sizes = {
        xl: 20,
        lg: 18,
        md: 16,
        sm: 14
      };
      return sizes[this.computedSize];
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal;
      this.setInnerText();
    },
    innerValue(newVal) {
      this.setInnerText();
      this.$emit("input", newVal);
    },
    options: {
      deep: true,
      handler() {
        this.setInnerText();
      }
    }
  },
  mounted() {
    this.setInnerText();
  }
};
</script>
<style lang="scss" scoped>
.bp-select {
  width: 100%;
  display: block;
  position: relative;

  .bp-select__label {
    margin: 0 0 4px;
    color: #666666;
  }
  .bp-select__container {
    border-radius: 4px;
    border: 1px solid #a9a9a9;
    transition: 0.3s all ease-in-out;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: 0.2s border-color;
    .bp-select__inner {
      border: none;
      outline: none;

      flex: 1;
      padding: 0;
      background: transparent;

      overflow: hidden;
      text-overflow: ellipsis;

      &::placeholder {
        color: #a9a9a9;
      }

      &:read-only {
        cursor: pointer;
      }
    }
  }
  .bp-select__options {
    width: 100%;
    padding: 8px;
    z-index: 100;
    overflow-y: auto;
    max-height: 250px;
    border-radius: var(--4-8, 4px);
    border: var(--1-2, 1px) solid
      var(--std-Neut-x-048, rgba(215, 215, 215, 0.48));
    background: var(--Surface-Cumulative-plus16, rgba(255, 255, 255, 0.64));
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(12px);
    gap: 4px;
    display: flex;
    flex-direction: column;
    .bp-select__option-cell {
      border-radius: 4px;
      color: rgba(102, 102, 102, 1);
      width: 100%;
      padding: 4px 12px;
      transition: 0.2s all;
      cursor: pointer;

      &.disabled {
        color: rgba(146, 146, 146, 1);
        cursor: default;
      }

      &:not(.disabled) {
        &:hover {
          background-color: rgba(215, 215, 215, 0.48);
        }
        &.selected {
          color: rgba(84, 0, 194, 1);
          background-color: rgba(215, 215, 215, 0.48);
        }
      }
    }
  }
  .bp-select__description-container {
    gap: 8px;
    display: flex;
    align-items: flex-start;
    .bp-select__description {
      flex: 1;
      color: #666666;
    }
  }
  // Sizes
  &.xl {
    .bp-select__container {
      padding: 16px;
    }
  }
  &.lg {
    .bp-select__container {
      padding: 12px;
    }
  }
  &.md {
    .bp-select__container {
      padding: 8px;
    }
  }
  &.sm {
    .bp-select__container {
      padding: 6px;
    }
  }

  // Desktop sizes
  @media screen and (min-width: 992px) {
    &.xl {
      .bp-select__container {
        padding: 9px;
      }
    }
    &.lg {
      .bp-select__container {
        padding: 8px;
      }
    }
    &.md {
      .bp-select__container {
        padding: 4px;
      }
    }
    &.sm {
      .bp-select__container {
        padding: 2px;
      }
    }
  }
  // States
  .bp-select__container:focus-within {
    border-color: #6700ee;
    background-color: white;
  }
  &.error {
    .bp-select__container {
      border-color: #ff5053;
    }
    .bp-select__description {
      color: #bf2e30;
    }
  }

  &.disabled {
    .bp-select__container {
      background-color: rgba(25, 25, 34, 0.0392156863);
      border-color: transparent;
      cursor: default;
      .bp-select__inner {
        color: $neutral-200;
        cursor: default;
      }
    }
  }
}
</style>
